import { FormFloatField, type FormFloatFieldProps } from '@mdvip/shared/components/blocks/FormFloatField';
import { Icon } from '@mdvip/shared/components/blocks/Icon';
import { InfoDot } from '@mdvip/shared/components/blocks/InfoDot';
import { Badge } from '@mdvip/shared/components/ui/badge';
import { Checkbox } from '@mdvip/shared/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@mdvip/shared/components/ui/radio-group';
import { useEffect } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { PaymentAccountFormValues } from '@/lib/payments/paymentAccount.types';

type AccountScope = 'card' | 'bank';

// Validation messages are dynamic i18n keys (from the zod schema), so bypass the strict typed-t.
function useTranslateError() {
  const { t } = useTranslation('members');
  return t as unknown as (message: string) => string;
}

/**
 * Members-scoped `FormFloatField`: binds the 'members' i18n namespace so validation messages
 * render translated, and fixes the form-values type. Call sites stay free of i18n wiring.
 */
export function TextField(props: Omit<FormFloatFieldProps<PaymentAccountFormValues>, 'translateError' | 'kind'>) {
  const translateError = useTranslateError();
  return <FormFloatField<PaymentAccountFormValues> {...props} translateError={translateError} />;
}

/** Members-scoped `FormFloatField` select variant (mirror of {@link TextField}). */
export function SelectField(props: Omit<FormFloatFieldProps<PaymentAccountFormValues>, 'translateError' | 'kind'>) {
  const translateError = useTranslateError();
  return <FormFloatField<PaymentAccountFormValues> {...props} kind="select" translateError={translateError} />;
}

/**
 * HSA Yes/No radio bound to a boolean field. `disabled` is for the Edit modal: legacy
 * locks HSA (and Default, and the billing Change button) whenever the currently-typed expiry is
 * in the past — submission is blocked anyway, so the rest of the form goes inert until expiry is
 * fixed. Add never needs this (a fresh card's expiry is validated before advancing).
 */
export function HsaRadioField({ scope, disabled }: { scope: AccountScope; disabled?: boolean }) {
  const { control } = useFormContext<PaymentAccountFormValues>();
  const { t } = useTranslation('members');

  return (
    <Controller
      control={control}
      name={`${scope}.isHsa`}
      render={({ field }) => (
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
          <span className="flex items-center gap-1 text-sm font-medium text-muted-foreground">
            {t('payments.addAccount.hsa')}
            <InfoDot text={t('payments.addAccount.hsaInfo')} />
          </span>
          <RadioGroup
            className="flex w-auto flex-row gap-4"
            value={field.value ? 'yes' : 'no'}
            onValueChange={(value) => field.onChange(value === 'yes')}
            disabled={disabled}
          >
            <label htmlFor={`${scope}-hsa-yes`} className="flex items-center gap-2 text-sm">
              <RadioGroupItem id={`${scope}-hsa-yes`} value="yes" /> {t('payments.addAccount.yes')}
            </label>
            <label htmlFor={`${scope}-hsa-no`} className="flex items-center gap-2 text-sm">
              <RadioGroupItem id={`${scope}-hsa-no`} value="no" /> {t('payments.addAccount.no')}
            </label>
          </RadioGroup>
        </div>
      )}
    />
  );
}

/**
 * AutoPay checkbox. Locks checked when the member is already enrolled or when the modal was
 * opened from AutoPay enrollment (`fromAutopay` — shows an "Enrolling" badge instead, so the two
 * locked reasons read differently). `member.autoPay` is a member-level flag with no card linkage,
 * so the locked-checked display is global — every card shows it once the member is enrolled, not
 * just the actual default one. That's cosmetic only: the checkbox is disabled and can't be
 * interacted with in that state. `disabled` is an additional, unrelated lock reason (the Edit
 * modal's expiry-in-the-past inert state) that never forces the value, only disables interaction.
 *
 * Checking this live always forces Default on too (`${scope}.makeDefault`) — checking AutoPay
 * yourself always implies you want this card to be the new default/AutoPay method. Unchecking
 * never cascades the other way.
 */
export function AutoPayCheckbox({
  scope,
  autoPayEnrolled,
  fromAutopay = false,
  disabled = false,
}: {
  scope: AccountScope;
  autoPayEnrolled: boolean;
  fromAutopay?: boolean;
  disabled?: boolean;
}) {
  const { control, setValue } = useFormContext<PaymentAccountFormValues>();
  const { t } = useTranslation('members');
  const autoPayForced = autoPayEnrolled || fromAutopay;

  // `autoPayEnrolled` loads asynchronously (a real query) — if it resolves to `true` after
  // this checkbox has already rendered (or been toggled off), the visual locked-checked state above
  // would otherwise diverge from the actual submitted form value. Force the real value to match.
  useEffect(() => {
    if (autoPayForced) setValue(`${scope}.autoPay`, true);
  }, [autoPayForced, scope, setValue]);

  const autoPayId = `${scope}-autopay`;

  return (
    <div className="flex items-center gap-2.5">
      <Controller
        control={control}
        name={`${scope}.autoPay`}
        render={({ field }) => (
          <Checkbox
            id={autoPayId}
            disabled={autoPayForced || disabled}
            checked={autoPayForced ? true : field.value}
            onCheckedChange={(checked) => {
              const on = checked === true;
              field.onChange(on);
              if (on) setValue(`${scope}.makeDefault`, true);
            }}
          />
        )}
      />
      <label htmlFor={autoPayId} className="text-sm text-muted-foreground">
        {t('payments.addAccount.autoPay')}
      </label>
      <InfoDot text={t('payments.addAccount.autoPayInfo')} />
      {fromAutopay && (
        <Badge variant="muted" className="gap-1">
          <Icon name="lock" size="xs" /> {t('payments.addAccount.enrollingBadge')}
        </Badge>
      )}
    </div>
  );
}

/**
 * Make-default checkbox. Locks checked only when this account is already the current default
 * (`isCurrentDefault`) or via `disabled` (e.g., the Edit modal's expiry-in-the-past inert state).
 * Never reacts to AutoPay — a brand-new card (Add) never sets `isCurrentDefault`, so it's always
 * freely editable there; only `AutoPayCheckbox`'s live click or the form's own seed value affects
 * it in that context.
 */
export function MakeDefaultCheckbox({
  scope,
  isCurrentDefault = false,
  disabled = false,
}: {
  scope: AccountScope;
  isCurrentDefault?: boolean;
  disabled?: boolean;
}) {
  const { control, setValue } = useFormContext<PaymentAccountFormValues>();
  const { t } = useTranslation('members');

  useEffect(() => {
    if (isCurrentDefault) setValue(`${scope}.makeDefault`, true);
  }, [isCurrentDefault, scope, setValue]);

  const defaultId = `${scope}-make-default`;

  return (
    <div className="flex items-center gap-2.5">
      <Controller
        control={control}
        name={`${scope}.makeDefault`}
        render={({ field }) => (
          <Checkbox
            id={defaultId}
            disabled={isCurrentDefault || disabled}
            checked={isCurrentDefault ? true : field.value}
            onCheckedChange={(checked) => field.onChange(checked === true)}
          />
        )}
      />
      <label htmlFor={defaultId} className="text-sm text-muted-foreground">
        {t('payments.addAccount.makeDefault')}
      </label>
    </div>
  );
}
